import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { 
  TrendingUp, BookOpen, Calculator, Coins, BarChart3, 
  Menu, X, ArrowUpRight, ArrowDownRight, User, LogOut, Crown, Rocket,
  ClipboardCheck, Shield
} from "lucide-react";
import "@/App.css";

// Context
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Components
import AuthModal from "./components/auth/AuthModal";
import CookieConsent from "./components/CookieConsent";
import LandingPage from "./pages/LandingPage";
import LearningHub from "./pages/LearningHub";
import TutorialDetail from "./pages/TutorialDetail";
import MarketDashboard from "./pages/MarketDashboard";
import PaperTrading from "./pages/PaperTrading";
import Calculators from "./pages/Calculators";
import GoldGuide from "./pages/GoldGuide";
import PricingPage from "./pages/PricingPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import { DisclaimerPage, DisclaimerBanner } from "./pages/DisclaimerPage";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import StartHere from "./pages/StartHere";
import Blog from "./pages/Blog";
import AdminDashboard from "./pages/AdminDashboard";
import QuizPage from "./pages/QuizPage";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Ticker Tape Component
export const TickerTape = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  return (
    <div className="ticker-tape" data-testid="ticker-tape">
      <Marquee gradient={false} speed={40} pauseOnHover>
        {data.map((item, index) => (
          <div key={index} className="ticker-item">
            <span className="ticker-symbol">{item.symbol}</span>
            <span className="ticker-price">${item.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <span className={`ticker-change ${item.change_24h >= 0 ? 'positive' : 'negative'}`}>
              {item.change_24h >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {Math.abs(item.change_24h || 0).toFixed(2)}%
            </span>
          </div>
        ))}
      </Marquee>
    </div>
  );
};

// Navigation Component
const Navigation = ({ tickerData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, tier } = useAuth();
  
  const navLinks = [
    { path: "/start-here", label: "Start Here", icon: <Rocket size={18} /> },
    { path: "/learn", label: "Learning Hub", icon: <BookOpen size={18} /> },
    { path: "/markets", label: "Markets", icon: <BarChart3 size={18} /> },
    { path: "/trade", label: "Simulator", icon: <TrendingUp size={18} /> },
    { path: "/blog", label: "My Journey", icon: <BookOpen size={18} /> },
    { path: "/quizzes", label: "Quizzes", icon: <ClipboardCheck size={18} /> },
  ];

  const allNavLinks = navLinks;

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  const getTierBadge = () => {
    if (tier === 'premium') return { label: 'Premium', color: 'var(--secondary)' };
    if (tier === 'pro') return { label: 'Pro', color: 'var(--primary)' };
    return null;
  };

  const tierBadge = getTierBadge();
  
  return (
    <>
      <TickerTape data={tickerData} />
      <nav className="main-nav" data-testid="main-navigation">
        <div className="nav-container">
          <Link to="/" className="nav-logo" data-testid="nav-logo">
            <TrendingUp size={28} />
            <span>BlackGirlTrading-Academy</span>
          </Link>
          
          <div className={`nav-links ${isOpen ? 'open' : ''}`}>
            {allNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.icon}
                <span>{link.label}</span>
              </Link>
            ))}
            <Link
              to="/pricing"
              className={`nav-link pricing-link ${location.pathname === '/pricing' ? 'active' : ''}`}
              onClick={() => setIsOpen(false)}
              data-testid="nav-pricing"
            >
              <Crown size={18} />
              <span>Pricing</span>
            </Link>
            {!isAuthenticated && (
              <button
                className="mobile-sign-in-link"
                onClick={() => { setShowAuth(true); setIsOpen(false); }}
                data-testid="mobile-sign-in-btn"
              >
                <User size={18} />
                <span>Sign In / Register</span>
              </button>
            )}
            {isAuthenticated && (
              <>
                <Link
                  to="/dashboard"
                  className="nav-link mobile-user-link"
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-dashboard-link"
                >
                  <BarChart3 size={18} />
                  <span>My Dashboard</span>
                </Link>
                {user?.is_admin && (
                  <Link
                    to="/admin"
                    className="nav-link admin-nav-link"
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-admin-link"
                  >
                    <Shield size={18} />
                    <span>Admin Dashboard</span>
                  </Link>
                )}
                <button
                  className="mobile-logout-link"
                  onClick={() => { handleLogout(); setIsOpen(false); }}
                  data-testid="mobile-logout-btn"
                >
                  <LogOut size={18} />
                  <span>Sign Out</span>
                </button>
              </>
            )}
          </div>

          <div className="nav-actions">
            {isAuthenticated ? (
              <div className="user-menu-container">
                <button 
                  className="user-menu-btn"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  data-testid="user-menu-btn"
                >
                  <div className="user-avatar">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  {tierBadge && (
                    <span className="tier-badge-nav" style={{ background: tierBadge.color }}>
                      {tierBadge.label}
                    </span>
                  )}
                </button>
                
                {showUserMenu && (
                  <div className="user-dropdown" data-testid="user-dropdown">
                    <div className="user-info">
                      <span className="user-name">{user?.name}</span>
                      <span className="user-email">{user?.email}</span>
                    </div>
                    <div className="dropdown-divider" />
                    <Link to="/dashboard" className="dropdown-item" onClick={() => setShowUserMenu(false)} data-testid="dashboard-link">
                      <BarChart3 size={16} />
                      My Dashboard
                    </Link>
                    {user?.is_admin && (
                      <Link to="/admin" className="dropdown-item admin-link" onClick={() => setShowUserMenu(false)} data-testid="admin-dashboard-link">
                        <Shield size={16} />
                        Admin Dashboard
                      </Link>
                    )}
                    <Link to="/pricing" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                      <Crown size={16} />
                      {tier === 'free' ? 'Upgrade Plan' : 'Manage Plan'}
                    </Link>
                    <button className="dropdown-item logout" onClick={handleLogout} data-testid="logout-btn">
                      <LogOut size={16} />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button 
                className="sign-in-btn-prominent"
                onClick={() => setShowAuth(true)}
                data-testid="sign-in-btn"
                style={{
                  padding: '10px 28px',
                  fontSize: '15px',
                  fontWeight: 700,
                  background: '#00ff94',
                  color: '#0a0a0f',
                  border: 'none',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  letterSpacing: '0.3px',
                  whiteSpace: 'nowrap'
                }}
              >
                Sign In
              </button>
            )}
          </div>
          
          <button 
            className="mobile-menu-btn" 
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />

      <style>{`
        .nav-actions {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
          margin-left: 12px;
        }

        .btn-sm {
          padding: 8px 16px;
          font-size: 14px;
        }

        .sign-in-btn-prominent {
          padding: 10px 28px;
          font-size: 15px;
          font-weight: 700;
          background: var(--primary);
          color: #0a0a0f;
          border: none;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.25s ease;
          letter-spacing: 0.3px;
          white-space: nowrap;
        }
        .sign-in-btn-prominent:hover {
          background: #00e085;
          transform: translateY(-1px);
          box-shadow: 0 4px 20px rgba(0, 255, 148, 0.35);
        }

        .dropdown-item.admin-link {
          color: var(--primary);
          font-weight: 600;
        }

        .user-menu-container {
          position: relative;
        }

        .user-menu-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0, 255, 148, 0.1);
          border: 2px solid var(--primary);
          cursor: pointer;
          padding: 6px 12px 6px 6px;
          border-radius: 50px;
          transition: all 0.2s ease;
        }
        
        .user-menu-btn:hover {
          background: rgba(0, 255, 148, 0.2);
          box-shadow: 0 0 15px rgba(0, 255, 148, 0.3);
        }

        .user-avatar {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--primary);
          border-radius: 50%;
          font-weight: 700;
          font-size: 15px;
          color: #0a0a0f;
        }

        .tier-badge-nav {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          color: white;
        }

        .user-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          min-width: 220px;
          overflow: hidden;
          z-index: 100;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
        }

        .user-info {
          padding: 16px;
        }

        .user-name {
          display: block;
          font-weight: 600;
          margin-bottom: 2px;
        }

        .user-email {
          display: block;
          font-size: 13px;
          color: var(--text-muted);
        }

        .dropdown-divider {
          height: 1px;
          background: var(--border);
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          background: none;
          border: none;
          color: var(--text-secondary);
          font-size: 14px;
          text-decoration: none;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .dropdown-item:hover {
          background: var(--bg-subtle);
          color: var(--text-primary);
        }

        .dropdown-item.logout {
          color: var(--error);
        }

        .pricing-link {
          color: var(--gold) !important;
        }

        .mobile-sign-in-link {
          display: none;
          align-items: center;
          gap: 10px;
          padding: 14px 20px;
          margin-top: 12px;
          background: linear-gradient(135deg, #00ff94 0%, #00e085 100%);
          color: #0a0a0f;
          border: 2px solid #00ff94;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 800;
          cursor: pointer;
          width: 100%;
          justify-content: center;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 20px rgba(0, 255, 148, 0.4);
        }
        .mobile-sign-in-link:hover {
          background: linear-gradient(135deg, #00e085 0%, #00cc70 100%);
          box-shadow: 0 6px 25px rgba(0, 255, 148, 0.5);
          transform: translateY(-1px);
        }

        .admin-nav-link {
          display: none;
          color: var(--primary) !important;
          font-weight: 600 !important;
        }
        
        .mobile-user-link {
          display: none;
          color: var(--primary) !important;
          font-weight: 600 !important;
        }
        
        .mobile-logout-link {
          display: none;
          align-items: center;
          gap: 10px;
          padding: 14px 20px;
          margin-top: 8px;
          background: transparent;
          color: #ff4757;
          border: 2px solid #ff4757;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          width: 100%;
          justify-content: center;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          transition: all 0.2s ease;
        }
        .mobile-logout-link:hover {
          background: rgba(255, 71, 87, 0.1);
        }

        @media (max-width: 768px) {
          .nav-actions {
            display: none;
          }
          .mobile-sign-in-link {
            display: flex !important;
          }
          .admin-nav-link {
            display: flex !important;
          }
          .mobile-user-link {
            display: flex !important;
          }
          .mobile-logout-link {
            display: flex !important;
          }
          .nav-links.open {
            padding-bottom: 16px;
          }
        }
        @media (min-width: 769px) {
          .mobile-sign-in-link,
          .admin-nav-link {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
};

// Page Transition Wrapper
const PageWrapper = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
};

// Main App Content (needs to be inside AuthProvider and BrowserRouter)
const AppContent = () => {
  const [tickerData, setTickerData] = useState([]);
  const [showDisclaimer, setShowDisclaimer] = useState(() => {
    return !localStorage.getItem('disclaimerDismissed');
  });
  
  const dismissDisclaimer = () => {
    localStorage.setItem('disclaimerDismissed', 'true');
    setShowDisclaimer(false);
  };
  
  useEffect(() => {
    const fetchTickerData = async () => {
      try {
        const response = await axios.get(`${API}/market/all`);
        const allData = [
          ...(response.data.crypto || []).slice(0, 5),
          ...(response.data.stocks || []).slice(0, 3),
          ...(response.data.commodities || []).slice(0, 2),
        ];
        setTickerData(allData);
      } catch (error) {
        console.error("Error fetching ticker data:", error);
      }
    };
    
    fetchTickerData();
    const interval = setInterval(fetchTickerData, 60000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <>
      <ScrollToTop />
      {showDisclaimer && <DisclaimerBanner onDismiss={dismissDisclaimer} />}
      <Navigation tickerData={tickerData} />
      <main className="main-content">
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={<PageWrapper><LandingPage /></PageWrapper>} />
            <Route path="/start-here" element={<PageWrapper><StartHere /></PageWrapper>} />
            <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
            <Route path="/contact" element={<PageWrapper><ContactPage /></PageWrapper>} />
            <Route path="/learn" element={<PageWrapper><LearningHub /></PageWrapper>} />
            <Route path="/learn/:level/:tutorialId" element={<PageWrapper><TutorialDetail /></PageWrapper>} />
            <Route path="/markets" element={<PageWrapper><MarketDashboard /></PageWrapper>} />
            <Route path="/trade" element={<PageWrapper><PaperTrading /></PageWrapper>} />
            <Route path="/calculators" element={<PageWrapper><Calculators /></PageWrapper>} />
            <Route path="/gold-guide" element={<PageWrapper><GoldGuide /></PageWrapper>} />
            <Route path="/pricing" element={<PageWrapper><PricingPage /></PageWrapper>} />
            <Route path="/payment/success" element={<PageWrapper><PaymentSuccess /></PageWrapper>} />
            <Route path="/disclaimer" element={<PageWrapper><DisclaimerPage /></PageWrapper>} />
            <Route path="/terms-of-service" element={<PageWrapper><TermsOfService /></PageWrapper>} />
            <Route path="/privacy-policy" element={<PageWrapper><PrivacyPolicy /></PageWrapper>} />
            <Route path="/forgot-password" element={<PageWrapper><ForgotPassword /></PageWrapper>} />
            <Route path="/reset-password" element={<PageWrapper><ResetPassword /></PageWrapper>} />
            <Route path="/dashboard" element={<PageWrapper><Dashboard /></PageWrapper>} />
            <Route path="/blog" element={<PageWrapper><Blog /></PageWrapper>} />
            <Route path="/quizzes" element={<PageWrapper><QuizPage /></PageWrapper>} />
            <Route path="/admin" element={<PageWrapper><AdminDashboard /></PageWrapper>} />
          </Routes>
        </AnimatePresence>
      </main>
      <footer className="main-footer" data-testid="main-footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand-section">
              <p className="footer-brand">BlackGirlTrading-Academy</p>
              <p className="footer-tagline">Educational Trading Community by @BlackGirlsTrade_</p>
            </div>
            <div className="footer-social">
              <a href="https://www.tiktok.com/@BlackGirlsTrade_" target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-tiktok">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
              </a>
              <a href="https://www.instagram.com/BlackGirlsTrade_" target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-instagram">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="mailto:BlackGirlTradingAcademy@hotmail.com" className="social-icon" data-testid="footer-email">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
              </a>
            </div>
          </div>
          <div className="footer-links-grid">
            <div className="footer-col">
              <h4>Platform</h4>
              <Link to="/learn">Learning Hub</Link>
              <Link to="/markets">Markets</Link>
              <Link to="/trade">Paper Trading</Link>
              <Link to="/calculators">Calculators</Link>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <Link to="/about">About Adiajah</Link>
              <Link to="/contact">Contact Us</Link>
              <Link to="/pricing">Pricing</Link>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <Link to="/disclaimer">Disclaimer</Link>
              <Link to="/terms-of-service">Terms of Service</Link>
              <Link to="/privacy-policy">Privacy Policy</Link>
            </div>
          </div>
          <div className="footer-disclaimer">
            <p>
              <strong>Important:</strong> BlackGirlTrading-Academy is an educational community platform, not a financial advisory service. 
              We do not provide financial advice, investment recommendations, or trade signals. 
              All content is for educational and informational purposes only.
            </p>
            <p>
              Trading involves significant risk of loss. You should never invest money you cannot afford to lose. 
              Past performance is not indicative of future results. Always seek advice from a qualified financial advisor 
              and conduct your own research before making any investment decisions.
            </p>
            <p className="footer-legal">
              BlackGirlTrading-Academy is not authorised or regulated by the Financial Conduct Authority (FCA). 
              All paper trading uses simulated virtual money only.
            </p>
          </div>
          <p className="footer-copyright">© 2026 BlackGirlTrading-Academy by Adiajah @BlackGirlsTrade_. All rights reserved.</p>
        </div>
      </footer>
      
      <style>{`
        .footer-main {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 24px;
          margin-bottom: 24px;
          border-bottom: 1px solid var(--border);
        }
        
        .footer-brand-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .footer-brand {
          font-weight: 700;
          font-size: 1.25rem;
          color: var(--text-primary);
          margin: 0;
        }
        
        .footer-tagline {
          font-size: 13px;
          color: var(--text-muted);
          margin: 0;
        }
        
        .footer-social {
          display: flex;
          gap: 12px;
        }
        
        .social-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
          transition: all 0.2s;
        }
        
        .social-icon:hover {
          border-color: var(--primary);
          color: var(--primary);
          background: rgba(0, 255, 148, 0.1);
        }
        
        .footer-links-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 32px;
          padding-bottom: 24px;
          margin-bottom: 24px;
          border-bottom: 1px solid var(--border);
        }
        
        .footer-col h4 {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
        }
        
        .footer-col a {
          display: block;
          color: var(--text-muted);
          text-decoration: none;
          font-size: 14px;
          margin-bottom: 10px;
        }
        
        .footer-col a:hover {
          color: var(--primary);
        }
        
        .footer-disclaimer {
          background: var(--bg-subtle);
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 24px;
        }
        
        .footer-disclaimer p {
          font-size: 12px;
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 12px;
        }
        
        .footer-disclaimer p:last-child {
          margin-bottom: 0;
        }
        
        .footer-disclaimer strong {
          color: var(--warning);
        }
        
        .footer-legal {
          padding-top: 12px;
          border-top: 1px solid var(--border);
        }
        
        .footer-copyright {
          text-align: center;
          font-size: 12px;
          color: var(--text-muted);
          margin: 0;
        }
        
        @media (max-width: 768px) {
          .footer-main {
            flex-direction: column;
            gap: 20px;
            text-align: center;
          }
          
          .footer-social {
            justify-content: center;
          }
          
          .footer-links-grid {
            grid-template-columns: 1fr;
            text-align: center;
            gap: 24px;
          }
        }
      `}</style>
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
    </>
  );
};

function App() {
  return (
    <div className="App dark" data-testid="app-container">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
